import PropTypes from 'prop-types';
import parse from 'html-react-parser';

/**
 * Renders the page-level footnotes list.
 *
 * `items` is the flat `footnotes` array from the API (sibling of `body`,
 * not nested inside it). Numbering (`index`) is authoritative and computed
 * server-side across the whole page — this component only sorts and
 * displays it, it never recomputes it.
 *
 * Each item exposes:
 *  - id="footnote-{uuid}"        → jump target for the inline marker in RawHtml
 *  - a backlink to #footnote-ref-{uuid} → the inline marker's own id
 */
const Footnotes = ({ items = [] }) => {
  if (!items || items.length === 0) return null;

  const sorted = [...items].sort((a, b) => a.index - b.index);

  return (
    <section className="section section-default-width footnotes">
      <div className="section__inner">
        <div className="section__header">
          <div className="text-label footnotes__title">Notes</div>
        </div>
        <div className="section__body">
          <ol className="footnotes-list">
            {sorted.map((item) => (
              <li
                key={item.uuid}
                id={`footnote-${item.uuid}`}
                className="footnotes-item"
              >
                <span className="footnotes-item__index">{item.index}.</span>
                <div className="footnotes-item__text rich-content">
                  {parse(item.text)}
                </div>
                <a
                  href={`#footnote-ref-${item.uuid}`}
                  className="footnotes-backlink"
                  aria-label={`Back to reference ${item.index}`}
                >
                  ↩
                </a>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
};

Footnotes.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      uuid: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      index: PropTypes.number.isRequired,
    })
  ),
};

export default Footnotes;